import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  HeartPulse, Loader2, Plus, ArrowRight, Camera, ImagePlus, MapPin, Navigation,
  Trash2, Send, FileText, ShieldCheck, ExternalLink, CalendarDays, Clock, FolderOpen, CheckCircle2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { FileLightbox, isPdfFile } from "./FileLightbox";
import { useLang } from "../i18n/LanguageContext";

const STATUS_CLS = {
  open: "bg-amber-50 text-amber-700 border-amber-200",
  submitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
};
const isImg = (ct) => (ct || "").startsWith("image/");

const UploadBox = ({ inj, cat, label, files, editable, onUpload, onDelete, onOpen }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const camId = `pinj-cam-${cat}`, galId = `pinj-gal-${cat}`;
  const handle = async (e) => {
    const list = Array.from(e.target.files || []); e.target.value = "";
    if (!list.length) return;
    setBusy(true);
    try { for (const f of list) await onUpload(cat, f); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`pinj-cat-${cat}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-sm font-bold text-slate-700">{label}
          {files.length > 0 && <span className="mr-1 text-[10px] rounded-full bg-slate-100 text-slate-500 px-1.5">{files.length}</span>}
        </span>
        {editable && (
          <div className="flex items-center gap-1.5">
            <input id={camId} type="file" accept="image/*" capture="environment" className="hidden" onChange={handle} />
            <input id={galId} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={handle} data-testid={`pinj-upload-${cat}`} />
            <Button size="sm" variant="outline" disabled={busy} onClick={() => document.getElementById(camId).click()} className="h-8 gap-1 text-xs">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} {t("portal.injuries.camera")}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => document.getElementById(galId).click()} className="h-8 gap-1 text-xs">
              <ImagePlus className="h-3.5 w-3.5" /> {t("portal.injuries.gallery")}
            </Button>
          </div>
        )}
      </div>
      {files.length === 0 ? (
        <p className="text-xs text-slate-300 py-2 text-center">{t("portal.injuries.noPhotos")}</p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {files.map((f) => (
            <div key={f.file_id} className="relative group rounded-lg border border-slate-200 overflow-hidden bg-slate-50" data-testid={`pinj-att-${f.file_id}`}>
              <button type="button" onClick={() => onOpen(api.portalInjuryFileUrl(inj.id, f.file_id), f.name, isPdfFile(f.name, f.content_type))} className="block w-full">
                {isImg(f.content_type)
                  ? <img src={api.portalInjuryFileUrl(inj.id, f.file_id)} alt={f.name} className="h-20 w-full object-cover" />
                  : <div className="h-20 w-full flex items-center justify-center text-slate-400"><FileText className="h-6 w-6" /></div>}
              </button>
              {editable && (
                <button onClick={() => onDelete(f.file_id)} data-testid={`pinj-att-del-${f.file_id}`}
                  className="absolute top-1 left-1 rounded-full bg-white/90 text-rose-600 p-1 shadow">
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const InjuryView = ({ inj, editable, onUpload, onDelete, onLocate, onManualLink, onNotes, locating }) => {
  const { t } = useLang();
  const [lb, setLb] = useState(null);
  const openLb = (url, name, isPdf) => setLb({ url, name, isPdf });
  const loc = inj.location || {};
  const mapsLink = loc.gmaps_link || loc.manual_link || (loc.lat ? `https://maps.google.com/?q=${loc.lat},${loc.lng}` : "");
  const rc = inj.rider_categories || [];
  const cats = Object.entries(inj.categories || {}).filter(([k]) => rc.includes(k));
  const byCat = inj.attachments_by_category || {};
  return (
    <div className="space-y-3">
      {editable && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center" data-testid="pinj-getwell">
          <p className="font-heading text-lg font-extrabold text-emerald-800">{t("portal.injuries.getWellTitle")}</p>
          <p className="text-sm text-emerald-700">{t("portal.injuries.getWellSub1")}</p>
          <p className="text-sm text-emerald-700">{t("portal.injuries.getWellSub2")}</p>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
        <span className="rounded-lg bg-slate-100 px-2 py-1 font-mono-nums">{t("portal.injuries.injuryNumber")}: {inj.injury_number}</span>
        <span className="rounded-lg bg-slate-100 px-2 py-1 flex items-center gap-1"><CalendarDays className="h-3 w-3" />{inj.injury_date} · {inj.opened_day}</span>
        {inj.opened_time && <span className="rounded-lg bg-slate-100 px-2 py-1 flex items-center gap-1"><Clock className="h-3 w-3" />{inj.opened_time}</span>}
        <span className={`rounded-full border px-2 py-0.5 ${STATUS_CLS[inj.status]}`}>{t(`portal.injuries.status.${inj.status}`)}</span>
      </div>

      {/* Location */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 space-y-2" data-testid="pinj-location">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5"><MapPin className="h-4 w-4 text-emerald-600" /> {t("portal.injuries.accidentLocation")}</p>
        {editable && (
          <>
            <Button data-testid="pinj-locate-btn" onClick={onLocate} disabled={locating} variant="outline" className="h-9 gap-1.5 w-full border-emerald-200 text-emerald-700">
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />} {t("portal.injuries.sendMyLocation")}
            </Button>
            <Input data-testid="pinj-manual-link" defaultValue={loc.manual_link || ""} placeholder={t("portal.injuries.pasteMapLink")}
              onBlur={(e) => onManualLink(e.target.value)} className="h-9" dir="ltr" />
          </>
        )}
        {mapsLink && (
          <a href={mapsLink} target="_blank" rel="noreferrer" data-testid="pinj-map-link"
            className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> {t("portal.injuries.viewOnMap")}
          </a>
        )}
      </div>

      {/* Auto docs */}
      {inj.auto_docs?.length > 0 && (
        <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-3">
          <p className="text-sm font-bold text-teal-800 mb-2 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> {t("portal.injuries.yourDocs")}</p>
          <div className="flex flex-wrap gap-2">
            {inj.auto_docs.map((d) => {
              const tr = t(`portal.docTypes.${d.key}`);
              const dl = tr.startsWith("portal.") ? d.label : tr;
              return (
                <button key={d.file_id} type="button" onClick={() => openLb(api.portalInjuryFileUrl(inj.id, d.file_id), dl, isPdfFile(d.name))} data-testid={`pinj-autodoc-${d.key}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-teal-300 bg-white px-3 py-1 text-xs text-teal-700">
                  <FileText className="h-3.5 w-3.5" /> {dl}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {editable && (
        <Textarea data-testid="pinj-notes" defaultValue={inj.notes || ""} placeholder={t("portal.injuries.notesPlaceholder")}
          onBlur={(e) => onNotes(e.target.value)} className="min-h-[70px]" />
      )}

      <div className="space-y-2">
        {cats.map(([cat, label]) => {
          const cl = t(`portal.injuries.cats.${cat}`);
          return (
            <UploadBox key={cat} inj={inj} cat={cat} label={cl.startsWith("portal.") ? label : cl} files={byCat[cat] || []}
              editable={editable} onUpload={onUpload} onDelete={onDelete} onOpen={openLb} />
          );
        })}
      </div>
      <FileLightbox open={!!lb} onOpenChange={(o) => !o && setLb(null)} url={lb?.url} name={lb?.name} isPdf={lb?.isPdf} />
    </div>
  );
};

export const PortalInjuries = () => {
  const { t } = useLang();
  const [tab, setTab] = useState("list");
  const [list, setList] = useState(null);
  const [current, setCurrent] = useState(null);   // draft/new injury
  const [viewing, setViewing] = useState(null);    // an existing injury (read)
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);

  const loadList = useCallback(async () => {
    try { const d = await api.portalListInjuries(); setList(d.items || []); } catch { toast.error(t("portal.injuries.loadFail")); }
  }, [t]);
  useEffect(() => { loadList(); }, [loadList]);

  const startNew = async () => {
    setBusy(true);
    try { const inj = await api.portalOpenInjury(); setCurrent(inj); setTab("new"); }
    catch (e) { toast.error(e?.response?.data?.detail || t("portal.injuries.openFail")); }
    finally { setBusy(false); }
  };
  const refreshCurrent = async () => { try { setCurrent(await api.portalGetInjury(current.id)); } catch {} };
  const upload = async (cat, file) => {
    try { await api.portalUploadInjuryPhoto(current.id, cat, file); await refreshCurrent(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("portal.injuries.uploadFail")); }
  };
  const delPhoto = async (fid) => {
    try { await api.portalDeleteInjuryPhoto(current.id, fid); await refreshCurrent(); }
    catch { toast.error(t("portal.injuries.deleteFail")); }
  };
  const locate = () => {
    if (!navigator.geolocation) { toast.error(t("portal.injuries.locationUnsupported")); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      try {
        await api.portalUpdateInjury(current.id, { location: { lat, lng, gmaps_link: `https://maps.google.com/?q=${lat},${lng}` } });
        await refreshCurrent(); toast.success(t("portal.injuries.locationSaved"));
      } catch { toast.error(t("portal.injuries.locationSaveFail")); }
      finally { setLocating(false); }
    }, () => { setLocating(false); toast.error(t("portal.injuries.locationFetchFail")); }, { enableHighAccuracy: true, timeout: 10000 });
  };
  const manualLink = async (link) => {
    const cur = current.location || {};
    if ((cur.manual_link || "") === (link || "")) return;
    try { await api.portalUpdateInjury(current.id, { location: { ...cur, manual_link: link } }); await refreshCurrent(); } catch {}
  };
  const saveNotes = async (notes) => {
    if ((current.notes || "") === (notes || "")) return;
    try { await api.portalUpdateInjury(current.id, { notes }); } catch {}
  };
  const submit = async () => {
    if (!window.confirm(t("portal.injuries.confirmSend"))) return;
    setBusy(true);
    try {
      await api.portalSubmitInjury(current.id);
      toast.success(t("portal.injuries.sentOk"));
      setCurrent(null); setTab("list"); await loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || t("portal.injuries.sendFail")); }
    finally { setBusy(false); }
  };
  const openView = async (id) => {
    try { setViewing(await api.portalGetInjury(id)); } catch { toast.error(t("portal.injuries.openViewFail")); }
  };

  return (
    <div className="space-y-3" data-testid="portal-injuries">
      <div className="flex items-center gap-2">
        <button data-testid="pinj-tab-list" onClick={() => { setTab("list"); setViewing(null); }}
          className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold border ${tab === "list" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200"}`}>
          {t("portal.injuries.myInjuries")}
        </button>
        <button data-testid="pinj-tab-new" onClick={startNew} disabled={busy}
          className={`flex-1 rounded-xl px-3 py-2 text-sm font-bold border ${tab === "new" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200"}`}>
          <span className="inline-flex items-center gap-1.5 justify-center">{busy && tab !== "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("portal.injuries.openNew")}</span>
        </button>
      </div>

      {tab === "new" && current ? (
        <div className="space-y-3">
          <button data-testid="pinj-back-list" onClick={() => { setCurrent(null); setTab("list"); loadList(); }} className="inline-flex items-center gap-1 text-sm text-slate-500">
            <ArrowRight className="h-4 w-4" /> {t("portal.injuries.back")}
          </button>
          <InjuryView inj={current} editable onUpload={upload} onDelete={delPhoto} onLocate={locate}
            onManualLink={manualLink} onNotes={saveNotes} locating={locating} />
          <Button data-testid="pinj-submit-btn" onClick={submit} disabled={busy} className="w-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {t("portal.injuries.sendToAdmin")}
          </Button>
        </div>
      ) : viewing ? (
        <div className="space-y-3">
          <button data-testid="pinj-back-view" onClick={() => setViewing(null)} className="inline-flex items-center gap-1 text-sm text-slate-500">
            <ArrowRight className="h-4 w-4" /> {t("portal.injuries.back")}
          </button>
          <InjuryView inj={viewing} editable={false} />
        </div>
      ) : list === null ? (
        <div className="py-12 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : list.length === 0 ? (
        <div className="py-12 text-center text-slate-400 space-y-1">
          <HeartPulse className="mx-auto h-8 w-8 text-slate-300" />
          <p className="text-sm">{t("portal.injuries.emptyList")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((i) => (
            <button key={i.id} data-testid={`pinj-item-${i.id}`} onClick={() => openView(i.id)}
              className="w-full text-right rounded-2xl border border-slate-200 bg-white p-3 hover:border-rose-300 transition">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-bold text-slate-800">
                  <FolderOpen className="h-4 w-4 text-rose-400" /> {i.injury_number}
                </span>
                <span className={`text-[11px] rounded-full border px-2 py-0.5 ${STATUS_CLS[i.status]}`}>
                  {i.status === "submitted" && <CheckCircle2 className="inline h-3 w-3 ml-0.5" />}{t(`portal.injuries.status.${i.status}`)}
                </span>
              </div>
              <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
                <CalendarDays className="h-3.5 w-3.5" />{i.injury_date} · {i.opened_day}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
